/**
 * Blocked drawer (DNKADM-0389 U3) — click a Blocked band on the Day grid and see what it
 * is, in the same shell and position as `ReservationDrawer`: the target (bay or coach), the
 * date, the blocked time range(s) in 12-hour clock, the resulting open hours that day, and
 * who created the exception and when, read from the config-change audit
 * (`GET /v2/admin/config-audit`, OWNER/ADMIN only — a `403` or any failure simply omits the
 * line; nothing is guessed). An all-day closure says "Closed all day".
 *
 * Actions: **Remove this block** — the same per-row DELETE the Block off dialog uses, behind
 * an inline confirm — and **Edit in Block off**, which hands off to the dialog on this date.
 * Everything rendered is read from the data the board already holds plus that one audit read.
 */
import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, Drawer, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { resolveCalendarMaps } from '@/theme/maps/calendar';
import { listConfigAudit } from '@/api/configAudit';
import { deleteResourceDateException } from '@/api/resources';
import { deleteStaffDateException } from '@/api/staff';
import { serverMessage } from '@/api/serverMessage';
import { useResource } from '@/hooks/useResource';
import type { ShiftLike } from '@/utils/shiftValidation';
import { dateLabel } from '@/utils/clockTime';
import type { HourRange } from './derive';
import { blockedRangesLabel, openHoursLabel } from './blockedLabels';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';

/** What the board knows about the clicked block — every line the drawer shows comes from here. */
export interface BlockedDrawerBlock {
  kind: 'bay' | 'coach';
  targetId: string;
  targetName: string;
  /** The visible `YYYY-MM-DD`. */
  date: string;
  /** The exception row id (used to DELETE and to find its audit entry), or null when unknown. */
  exceptionId: string | null;
  isAvailable: boolean;
  shifts: ShiftLike[];
  /** The day's operating window — the blocked ranges are the gaps inside it. */
  operating: HourRange | null;
}

export interface BlockedDrawerProps {
  /** The selected block, or null (drawer closed). */
  block: BlockedDrawerBlock | null;
  /** Whether this session may remove the block (the staff floor cannot). */
  canWrite: boolean;
  onClose: () => void;
  /** The block was deleted — the board should re-read its blocks. */
  onRemoved: () => void;
  /** Open the Block off dialog on this date. */
  onEdit: () => void;
}

const AUDIT_ENTITY: Record<BlockedDrawerBlock['kind'], string> = {
  bay: 'v2_resource_date_exception',
  coach: 'v2_staff_date_exception',
};

/** The audit timestamp the way the Config-change log shows it: the venue's clock (DNKADM-0476 F-05). */
function formatWhen(iso: string, timeZone: string | null): string {
  return formatInstant(iso, timeZone, DATE_TIME);
}

export function BlockedDrawer({ block, canWrite, onClose, onRemoved, onEdit }: BlockedDrawerProps) {
  // The body is keyed by the block's identity, so its confirm / error / removing state and its
  // audit read belong to THAT block and reset when another band is tapped or the drawer closes
  // (the parent may swap the block without passing through `onClose`, e.g. Edit).
  const key = block ? `${block.kind}:${block.targetId}:${block.exceptionId ?? ''}:${block.date}` : null;
  return (
    <Drawer
      anchor="right"
      open={block !== null}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 380, maxWidth: '100vw' } } }}
    >
      {block && key && (
        <BlockedDrawerBody
          key={key}
          block={block}
          canWrite={canWrite}
          onClose={onClose}
          onRemoved={onRemoved}
          onEdit={onEdit}
        />
      )}
    </Drawer>
  );
}

function BlockedDrawerBody({
  block,
  canWrite,
  onClose,
  onRemoved,
  onEdit,
}: Omit<BlockedDrawerProps, 'block'> & { block: BlockedDrawerBlock }) {
  const cm = resolveCalendarMaps(useTheme());
  const calendarZone = useCalendarZone();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { kind, exceptionId } = block;
  // Who created it and when — one audit read per opened block, fail-soft: a `403` (the staff
  // floor) or any failure leaves the line out rather than guessing.
  const audit = useResource(
    useCallback(async () => {
      if (!exceptionId) return null;
      try {
        const page = await listConfigAudit({ entityType: AUDIT_ENTITY[kind], size: 50 });
        return (
          page.entries.find((e) => e.entityKey === exceptionId && e.action === 'create') ?? null
        );
      } catch {
        return null;
      }
    }, [kind, exceptionId]),
    [kind, exceptionId],
  );
  // Only a settled read renders the line — never a previous block's creator mid-load.
  const created = audit.status === 'ready' ? audit.data : null;

  const lines = useMemo(() => ({ blocked: blockedRangesLabel(block), open: openHoursLabel(block) }), [block]);

  async function handleRemove() {
    if (block.exceptionId === null) return;
    setRemoving(true);
    setError(null);
    try {
      if (block.kind === 'bay') await deleteResourceDateException(block.targetId, block.exceptionId);
      else await deleteStaffDateException(block.targetId, block.exceptionId);
      setConfirming(false);
      onRemoved();
      onClose();
    } catch (err) {
      setError(serverMessage(err) ?? 'The block couldn’t be removed.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <>
      {(
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="blocked-drawer">
          <Box sx={{ bgcolor: cm.neutralStatusBg, px: 2.5, py: 2.25 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Blocked
              </Typography>
              <IconAction size="small" onClick={onClose} label="Close" disabled={removing} sx={{ mt: -0.5, mr: -0.5 }}>
                <Icon name="close" sx={{ fontSize: 22 }} />
              </IconAction>
            </Stack>
            <Typography component="h2" sx={{ fontSize: 24, fontWeight: 500, mt: 0.75 }}>
              {block.targetName}
            </Typography>
            <Typography sx={{ fontSize: 14, opacity: 0.92, mt: 0.25 }}>{dateLabel(block.date) ?? block.date}</Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 1.5 }}>
            <Row label="Blocked" value={lines.blocked} cmRowBorder={cm.rowBorder} muted={cm.textMuted} />
            <Row label="Open" value={lines.open} cmRowBorder={cm.rowBorder} muted={cm.textMuted} />
            {created && created.actor && created.timestamp && (
              <Row
                label="Created"
                value={`by ${created.actor} · ${formatWhen(created.timestamp, calendarZone)}`}
                cmRowBorder={cm.rowBorder}
                muted={cm.textMuted}
                last
              />
            )}
            {error && (
              <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            )}
            {confirming && (
              <Alert
                severity="warning"
                sx={{ mt: 1.5 }}
                data-testid="blocked-drawer-confirm"
                action={
                  <Stack direction="row" sx={{ gap: 0.5 }}>
                    <Button color="inherit" size="small" onClick={() => setConfirming(false)} disabled={removing}>
                      Keep it
                    </Button>
                    <Button color="inherit" size="small" variant="outlined" onClick={() => void handleRemove()} disabled={removing}>
                      {removing ? 'Removing…' : 'Remove'}
                    </Button>
                  </Stack>
                }
              >
                Remove the block on {block.targetName} for {dateLabel(block.date) ?? block.date}?
              </Alert>
            )}
          </Box>

          {canWrite && (
            <Stack direction="row" sx={{ gap: 1, px: 2.5, py: 2, borderTop: `1px solid ${cm.rowBorder}` }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setConfirming(true)}
                disabled={block.exceptionId === null || confirming || removing}
                sx={{ flex: 1 }}
              >
                Remove this block
              </Button>
              <Button variant="contained" onClick={onEdit} disabled={removing} sx={{ flex: 1 }}>
                Edit in Block off
              </Button>
            </Stack>
          )}
        </Box>
      )}
    </>
  );
}

function Row({
  label,
  value,
  cmRowBorder,
  muted,
  last = false,
}: {
  label: string;
  value: string;
  cmRowBorder: string;
  muted: string;
  last?: boolean;
}) {
  return (
    <Stack direction="row" sx={{ py: '11px', borderBottom: last ? 'none' : `1px solid ${cmRowBorder}` }}>
      <Box sx={{ width: 110, flex: 'none', fontSize: 12, color: muted, fontWeight: 500 }}>{label}</Box>
      <Typography sx={{ fontSize: 13.5, flex: 1 }}>{value}</Typography>
    </Stack>
  );
}
